import React from 'react';

import Badge from '@atlaskit/badge';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import PanelErrorIcon from '@atlaskit/icon-lab/core/cross-octagon';
import PanelWarnIcon from '@atlaskit/icon-lab/core/vulnerability';
import PanelNoteIcon from '@atlaskit/icon/core/feed';
import TableIcon from '@atlaskit/icon/core/spreadsheet';
import TextSpellcheckIcon from '@atlaskit/icon/core/text-spellcheck';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EditorMoreIcon from '@atlaskit/icon/glyph/editor/more';
import PanelInfoIcon from '@atlaskit/icon/glyph/editor/panel';
import PresenceActiveIcon from '@atlaskit/icon/glyph/presence-active';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { type TransformAction } from '../pm-plugins/ai-proactive/api';

const headingStyles = xcss({
	flex: 1,
});

const pressableStyles = xcss({
	color: 'color.text.subtle',
	fontWeight: 'font.weight.medium',
	padding: 'space.0',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text',
	},
});

const pressableSelectedStyles = xcss({
	textDecoration: 'underline',
});

const containerStyles = xcss({
	display: 'flex',
	paddingInline: 'space.100',
	paddingBlock: 'space.200',
	flexDirection: 'column',
	gap: 'space.050',
	alignSelf: 'stretch',
	backgroundColor: 'elevation.surface',
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
});

const containerSelectedStyles = xcss({
	backgroundColor: 'elevation.surface.pressed',
});

type ConversionDetails = {
	icon: React.ElementType;
	description: string;
	message: string;
};

const defaultDetails: ConversionDetails = {
	icon: PresenceActiveIcon,
	message: 'Suggestion',
	description: 'A suggestion to improve the content.',
};

const detailsByConversionType: Record<TransformAction, ConversionDetails> = {
	CONVERT_TO_TABLE: {
		icon: TableIcon,
		message: 'Convert to a table',
		description: 'Convert the selected content to a table.',
	},
	CONVERT_TO_NOTE_PANEL: {
		icon: PanelNoteIcon,
		message: 'Convert to a note panel',
		description: 'Convert the selected content to a note panel.',
	},
	CONVERT_TO_INFO_PANEL: {
		icon: PanelInfoIcon,
		message: 'Convert to a info panel',
		description: 'Convert the selected content to an info panel.',
	},
	CONVERT_TO_WARN_PANEL: {
		icon: PanelWarnIcon,
		message: 'Convert to a warn panel',
		description: 'Convert the selected content to a warn panel.',
	},
	CONVERT_TO_ERROR_PANEL: {
		icon: PanelErrorIcon,
		message: 'Convert to an error panel',
		description: 'Convert the selected content to an error panel.',
	},
};

type ListItemProps = {
	title: string;
	description?: string;
	onClick: () => void;
	onHover?: () => void;
	beforeIcon: React.ReactNode;
	afterIcon: React.ReactNode;
	actionButtons?: React.ReactNode[];
	isSelected?: boolean;
};
const ListItem = (props: ListItemProps) => {
	const { title, description, onClick, onHover, isSelected, beforeIcon, afterIcon, actionButtons } =
		props;
	return (
		<Stack xcss={[containerStyles, isSelected && containerSelectedStyles]}>
			<Box onClick={onClick} onMouseEnter={onHover}>
				<Inline space="space.150" alignBlock="center">
					<Box>
						<Tooltip content={title} position="top">
							{beforeIcon}
						</Tooltip>
					</Box>
					<Box xcss={headingStyles}>
						<Heading size="xsmall">{title}</Heading>
					</Box>
					{afterIcon}
				</Inline>
			</Box>
			{!!description && <Text color="color.text.subtle">{description}</Text>}
			{!!actionButtons?.length && (
				<Inline space="space.150" alignBlock="center">
					{actionButtons}
				</Inline>
			)}
		</Stack>
	);
};

type RecommendationItemProps = {
	id: string;
	action: TransformAction;
	isViewed: boolean;
	isHovered: boolean;
	isSelected: boolean;
	onApply: () => void;
	onDismiss: () => void;
	onClick: () => void;
	onHover: () => void;
	onPreview: () => void;
};

export const RecommendationItem = (props: RecommendationItemProps) => {
	const {
		action,
		isViewed,
		isHovered,
		isSelected,
		onApply,
		onDismiss,
		onClick,
		onHover,
		onPreview,
	} = props;
	const { icon: Icon, description, message } = detailsByConversionType[action] ?? defaultDetails;
	const applyButton = (
		<Pressable
			xcss={[pressableStyles, isHovered && pressableSelectedStyles]}
			onClick={onPreview}
			key="apply"
		>
			Apply
		</Pressable>
	);

	const insertButton = (
		<Pressable xcss={pressableStyles} onClick={onApply} key="insert">
			Insert
		</Pressable>
	);

	const dismissButton = (
		<Pressable xcss={pressableStyles} onClick={onDismiss} key="dismiss">
			Dismiss
		</Pressable>
	);

	const actionButtons = isSelected ? [insertButton, dismissButton] : [applyButton, dismissButton];

	return (
		<ListItem
			title={message}
			description={description}
			isSelected={isSelected}
			onClick={onClick}
			onHover={onHover}
			beforeIcon={<Icon size="medium" label={message} primaryColor={token('color.icon')} />}
			afterIcon={
				isViewed ? null : (
					<PresenceActiveIcon
						size="small"
						label="unread"
						primaryColor={token('color.icon.information')}
					/>
				)
			}
			actionButtons={actionButtons}
		/>
	);
};

type SpellingGrammarItemProps = {
	errorCount: number;
	onClick: () => void;
};

export const SpellingGrammarItem = (props: SpellingGrammarItemProps) => {
	const { errorCount, onClick } = props;
	const message = 'Spelling and Grammar';
	return (
		<ListItem
			title={message}
			onClick={onClick}
			beforeIcon={<TextSpellcheckIcon label={message} color={token('color.icon')} />}
			afterIcon={<Badge>{errorCount}</Badge>}
		/>
	);
};

type HeadingItemProps = {
	onClose: () => void;
};

export const HeadingItem = (props: HeadingItemProps) => {
	const { onClose } = props;
	const message = 'Suggested edits';
	return (
		<Inline space="space.150" alignBlock="center">
			<Box xcss={headingStyles}>
				<Heading size="small" color="color.text">
					{message}
				</Heading>
			</Box>
			<IconButton appearance="subtle" label={'messages.more'} icon={EditorMoreIcon} />
			<IconButton appearance="subtle" label={'messages.close'} icon={CrossIcon} onClick={onClose} />
		</Inline>
	);
};
